import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";

const iStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none" };

const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: error ? "#C53030" : "#2D3748", marginBottom: 5, display: "block" }}>{label}</label>
    {children}
    {error && <div style={{ fontSize: 11, color: "#C53030", marginTop: 4 }}>{error}</div>}
  </div>
);

const EditShgMember = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const res = await axios.get(`${Api}/api/shgMember/${id}`, config);
        setData(res.data.shgMembers);
      } catch (e) {
        console.error("Error fetching SHG member", e);
        toast.error("Failed to fetch member data.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, Api]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const config = Authorization();
      await axios.put(`${Api}/api/shgMember/${id}`, data, config);
      toast.success("SHG member updated successfully");
      navigate("/SHG/members");
    } catch (err) {
      console.error("Error updating SHG member", err);
      toast.error("Failed to update member.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Preloader loading={loading} />;

  if (!data) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>Member Not Found</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>The requested SHG member record could not be loaded.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Edit SHG Member</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>SHG</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Edit Member</span>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {/* Card header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
            <i className="mdi mdi-account-edit" />
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>SHG Member Edit Form</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Update designation and status for this member</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
          {/* Member identity (read-only) */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "#F7FAF7", borderRadius: 10, border: "1px solid #E8F5E9", marginBottom: 20 }}>
            <span style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#1B5E20,#43A047)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
              {data.member?.name?.charAt(0)?.toUpperCase() || "?"}
            </span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1A202C" }}>{data.member?.name || "—"}</div>
              <div style={{ fontSize: 11, color: "#718096" }}>{data.shg?.name || "Unknown SHG"}</div>
            </div>
            <span style={{ marginLeft: "auto", background: data.status === "A" ? "#E8F5E9" : "#FFF5F5", color: data.status === "A" ? "#1B5E20" : "#E53E3E", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 50, border: `1px solid ${data.status === "A" ? "#A5D6A7" : "#FCA5A5"}` }}>
              {data.status === "A" ? "Active" : "Inactive"}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <Field label="Designation">
              <input
                name="designation"
                value={data.designation || ""}
                onChange={handleChange}
                placeholder="Enter designation"
                style={iStyle}
                onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
              />
            </Field>

            <Field label="Status">
              <select
                name="status"
                value={data.status || ""}
                onChange={handleChange}
                style={{ ...iStyle, cursor: "pointer" }}
                onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
              >
                <option value="">Select Status</option>
                <option value="A">Active</option>
                <option value="I">Inactive</option>
              </select>
            </Field>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 10, paddingTop: 8, borderTop: "1px solid #EDF2F7", marginTop: 8 }}>
            <button
              type="submit"
              disabled={submitting}
              style={{ padding: "9px 22px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(46,125,50,0.3)", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? <><i className="mdi mdi-loading mdi-spin" /> Saving…</> : <><i className="mdi mdi-content-save-outline" /> Save Changes</>}
            </button>
            <button
              type="button"
              onClick={() => navigate("/SHG/members")}
              style={{ padding: "9px 18px", background: "#F7FAFC", color: "#4A5568", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditShgMember;
